import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { resourceLines } from "app/utils/request/scheduling/facilityResources";

import ModalLayout from "../../components/Layouts/ModalLayout/ModalLayout";
import styles from "./styles.module.scss";
import CalendarIco from "../../../public/images/calendar-ico.svg";
import ClockIco from "../../../public/images/clock-ico.svg";
import DollarIco from "../../../public/images/dollar-ico.svg";
import LinkSquare from "../../../public/images/link-square.svg";
import i18n from "app/i18n";
import { useHistory } from "react-router-dom";
import OnlinePayment from "../../../public/images/online-payment-black.svg";
import OfflinePayment from "../../../public/images/offline-payment-black.svg";
import { cls } from "app/utils";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";
import useCopyToClipboard from "app/utils/hooks/useCopyToClipboard";
import UserAvatar from "app/components/UserAvatar";
import { ageFromBirthdate } from "app/utils/age";
import {
  participantFullName,
  sessionBadgeParticipants,
} from "./helpers/sessionParticipant";

enum PaymentStatusType {
  PENDING = "pending",
  PAID = "paid",
  FAILED = "failed",
  // PASTDUE = "past_due",
  CANCELLED = "cancelled",
}

export default function DetailsSession({
  isOpen,
  onClose,
  session,
  onEditSession,
  onCancelSession,
}: any) {
  const { t } = useTranslation();
  const history = useHistory();
  const { copyToClipboard } = useCopyToClipboard();

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case PaymentStatusType.PAID:
        return t("Paid");
      case PaymentStatusType.PENDING:
        return t("Pending");
      // case PaymentStatusType.PASTDUE:
      //   return t("Past Due");
      case PaymentStatusType.CANCELLED:
        return t("Canceled");
      default:
        return t("Failed");
    }
  };

  const cancelButton = (
    <button
      data-testid="cancel-edit"
      className={`${styles.button} ${styles.cancel_button}`}
      onClick={onCancelSession}
    >
      {t("Cancel Session")}
    </button>
  );

  const editButton = (
    <button
      className={`${styles.button} ${styles.confirm_button}`}
      type="submit"
      data-testid="session-edit-btn"
      onClick={() => onEditSession(session)}
    >
      {t("Edit Session")}
    </button>
  );

  const onViewMediaButtonClick = () => {
    if (session.feedPostId) {
      const targetUrl = `/posts/${session.feedPostId}`;
      history.push(targetUrl);
    }
  };

  const viewMediaButton = (
    <button
      className={`${styles.button} ${styles.all_line_button}`}
      onClick={() => onViewMediaButtonClick()}
    >
      {t("View Media")}
    </button>
  );

  const startDate = new Date(session.start);
  const formattedDate = startDate.toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const coachName =
    session.coachProfile.firstName + " " + session.coachProfile.lastName;
  const bayLines = resourceLines(session);

  // CN-1323 (CFTB-3279/3297 + CFTB-3336 follow-up): when the session involves a
  // managed family member, name everyone who attends — the dependents plus the
  // head when they're a participant too — so the viewer always knows who the
  // session/edit/cancel affects. Mirrors the participant badge.
  const participants = sessionBadgeParticipants(session);

  const paymentMethod = session.payment_method;
  const paymentStatus = session.payment_status;
  const isOnline = paymentMethod === "online";
  const isPackage = paymentMethod === "package";
  const isOnlineOrPackage = isOnline || isPackage;

  const invoiceUrl: string | null | undefined = session.invoiceUrl;
  const sharePaymentUrl: string | null | undefined = session.sharePaymentUrl;
  const showInvoiceUrlRow =
    isOnline &&
    typeof invoiceUrl === "string" &&
    invoiceUrl.length > 0;

  // Copy the shareable hosted URL when present (always the athlete-facing payment
  // link). Falls back to invoiceUrl for older payloads that don't carry it yet.
  const copyablePaymentUrl = sharePaymentUrl || invoiceUrl;

  const handleCopyPaymentLink = () => {
    if (copyablePaymentUrl) {
      copyToClipboard(copyablePaymentUrl);
      toast.success(t("Payment link copied"));
    }
  };

  const paymentLabel = isPackage
    ? t("Package / Subscription")
    : isOnline
      ? t("Online Payment")
      : t("Offline Payment");

  const PaymentIcon = isOnlineOrPackage ? OnlinePayment : OfflinePayment;

  const renderPaymentStatus = useCallback(() => {
    return (
      <div
        className={cls(
          styles.payment_status_pill,
          styles[`payment_status_${paymentStatus}`],
          { ["hidden"]: !paymentStatus },
        )}
      >
        {getStatusDisplayText(paymentStatus)}
      </div>
    );
  }, [paymentStatus, t]);

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal}>
      <div className={styles.modal_header}>
        <div className={styles.title}>{t("Details")}</div>
        <i
          className={`ico ico-x ${styles.modal_status_icon}`}
          onClick={onClose}
        />
      </div>
      <div className={styles.details}>
        {session.scheduleProviderDisplayName && (
          <div
            className={styles.external_provider}
          >{`${t("Booked via ")} ${session.scheduleProviderDisplayName}`}</div>
        )}
        <div className={styles.rows}>
          <div className={styles.circle}>
            <img src={CalendarIco} alt="date" className={styles.icon} />
          </div>
          <div className={styles.value}>{formattedDate}</div>
        </div>
        <div className={styles.rows}>
          <div className={styles.circle}>
            <img src={ClockIco} alt="time" className={styles.icon} />
          </div>
          <div className={styles.value}>{session.intervalTimeString}</div>
        </div>
        {/* CN-2711: a hidden-price booking option shows every other detail. */}
        {!session.lessonRateHidePrice && (
          <div className={styles.rows}>
            <div className={styles.circle}>
              <img src={DollarIco} alt="price" className={styles.icon} />
            </div>
            <div className={styles.value}>
              {generateCurrencySymbol(session?.rateCurrency) || session?.coachProfile?.currencySymbol || "$"}
              {session.rate}
            </div>

          </div>
        )}
        <div className={styles.rows}>
          <div className={styles.circle}>
            <img
              src={PaymentIcon}
              alt="payment method"
              className={styles.icon}
              color="black"
            />
          </div>

          {session.productName ?
            <div>
              <div className={styles.value}>{paymentLabel}</div>
              <div>{session.productName}</div>
            </div> :
            <div className={styles.value}>{paymentLabel}</div>
          }

          {renderPaymentStatus()}
        </div>
        {showInvoiceUrlRow && (
          <div className={styles.rows}>
            <div className={styles.circle}>
              <img src={LinkSquare} alt="invoice" className={cls(styles.icon, styles.invoice_icon)} />
            </div>
            <div className={cls(styles.value, styles.invoice_url_wrapper)}>
              <a
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.invoice_url_link}
              >
                {t("View invoice")}
              </a>
              {sharePaymentUrl && sharePaymentUrl !== invoiceUrl && (
                <>
                  <span className={styles.invoice_url_separator}>•</span>
                  <a
                    onClick={handleCopyPaymentLink}
                    className={styles.invoice_url_link}
                    style={{ cursor: "pointer" }}
                  >
                    {t("Copy payment link")}
                  </a>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {participants.length > 0 && (
        <div>
          <div className={styles.subtitle}>
            {participants.length > 1 ? t("Family Members") : t("Family Member")}
          </div>
          {participants.map((participant) => {
            const participantName = participantFullName(participant);
            const participantAge = ageFromBirthdate(participant.birthdate);

            return (
              <div key={participant.id ?? participantName} className={styles.coach_info}>
                <UserAvatar
                  sizeWidth={48}
                  sizeHeight={48}
                  altName={participantName}
                  srcName={participant.profilePicture ?? "default"}
                  userName={participantName}
                  className={cls("avatar", styles.participant_img)}
                />
                <div className={styles.coach_name}>
                  {participantName}
                  {participantAge != null && (
                    <span className={styles.participant_age}>
                      {" "}
                      · {t("Age {{age}}", { age: participantAge })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div>
        <div className={styles.subtitle}>{t("Coach")}</div>
        <div className={styles.coach_info}>
          <img
            src={session.coachProfile.profilePicture}
            alt="coach"
            className={styles.coach_img}
          />
          <div className={styles.coach_name}>{coachName}</div>
        </div>
      </div>
      {session.lessonRateTitle && (
        <div>
          <div className={styles.subtitle}>{t("Session")}</div>
          <div className={styles.note}>{session.lessonRateTitle}</div>
        </div>
      )}
      {bayLines.map((line) => (
        <div data-testid="session-resource" key={line.section}>
          <div className={styles.subtitle}>{line.section || t("Resource")}</div>
          <div className={styles.note}>{line.titles}</div>
        </div>
      ))}
      {session.notes && (
        <div>
          <div className={styles.subtitle}>{t("Notes")}</div>
          <div className={styles.note}>{session.notes}</div>
        </div>
      )}
      {!session.scheduleProviderDisplayName && !session.feedPostId && (
        <div className={styles.footer}>
          <div className={styles.buttons_container}>
            {cancelButton} {editButton}
          </div>
        </div>
      )}
      {session.feedPostId && (
        <div className={styles.footer}>
          <div className={styles.buttons_container}>{viewMediaButton}</div>
        </div>
      )}
    </ModalLayout>
  );
}
